import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, X, Settings, MessageSquare, Zap, Save } from "lucide-react";

export default function ChatbotSettingsPanel({
  flowConfig,
  onUpdate,
  onSave,
  saving,
  success,
  error,
  mode,
}) {
  const [localConfig, setLocalConfig] = useState(flowConfig);
  const [newTrigger, setNewTrigger] = useState("");

  useEffect(() => setLocalConfig(flowConfig), [flowConfig]);

  const handleChange = (field, value) => {
    onUpdate({ [field]: value });
    setLocalConfig((prev) => ({ ...prev, [field]: value }));
  };

  const addStartTrigger = () => {
    if (newTrigger.trim()) {
      const updatedTriggers = [...(localConfig.startTriggers || []), newTrigger.trim()];
      handleChange("startTriggers", updatedTriggers);
      setNewTrigger("");
    }
  };

  const removeStartTrigger = (index) => {
    const updatedTriggers = (localConfig.startTriggers || []).filter((_, i) => i !== index);
    if (updatedTriggers.length === 0) updatedTriggers.push("start");
    handleChange("startTriggers", updatedTriggers);
  };

  const updateStartTrigger = (index, value) => {
    const updatedTriggers = [...(localConfig.startTriggers || [])];
    updatedTriggers[index] = value;
    handleChange("startTriggers", updatedTriggers);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addStartTrigger();
    }
  };

  const addResponse = () => {
    setLocalConfig((prev) => ({
      ...prev,
      responses: [...(prev.responses || []), { trigger: "", action: "message", content: "" }],
    }));
  };

  const removeResponse = (index) => {
    setLocalConfig((prev) => ({
      ...prev,
      responses: (prev.responses || []).filter((_, i) => i !== index),
    }));
  };

  const updateResponse = (idx, field, value) => {
    setLocalConfig((prev) => {
      const updated = [...(prev.responses || [])];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, responses: updated };
    });
  };

  const handleSave = () => {
    if (!localConfig) return;
    onSave(
      localConfig.autoMessage,
      localConfig.responses,
      localConfig.botName,
      localConfig.startTriggers
    );
  };

  if (!localConfig) return null;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6 pb-24 bg-[#0B0F13] text-white min-h-screen">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-6"
      >
        <h1 className="text-2xl font-bold text-white mb-1">Chatbot Settings</h1>
        <p className="text-sm text-gray-300">Configure your chatbot's behavior and responses</p>
      </motion.div>

      {/* Bot Name Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/5 rounded-lg p-5 shadow-md border border-white/10"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-white/10 rounded-lg">
            <Settings className="w-4 h-4 text-emerald-300" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Bot Configuration</h3>
            <p className="text-xs text-gray-300">Set up your bot's identity</p>
          </div>
        </div>
        <div>
          <label htmlFor="bot-name" className="block mb-2 text-sm font-medium text-gray-300">
            Bot Name
          </label>
          <input
            id="bot-name"
            type="text"
            className="w-full border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white bg-white/5 focus:outline-none focus:ring-2 focus:ring-emerald-400/20 focus:border-emerald-400 transition-all placeholder-gray-400"
            value={localConfig.botName}
            onChange={(e) => handleChange("botName", e.target.value)}
            placeholder="Enter your bot's name"
            disabled={saving}
          />
        </div>
      </motion.div>

      {/* Start Triggers Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/5 rounded-lg p-3 shadow-md border border-white/10"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-white/10 rounded-lg">
            <Zap className="w-4 h-4 text-emerald-300" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Start Triggers</h3>
            <p className="text-xs text-gray-300">Keywords that activate your bot</p>
          </div>
        </div>
        
        {/* Existing Triggers */}
        <div className="space-y-1 mb-4">
          {(localConfig.startTriggers || []).map((trigger, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 bg-white/5 p-2 rounded-lg border border-white/10 hover:border-white/20 transition-colors"
            >
              <input
                type="text"
                className="flex-1 border-none outline-none bg-transparent text-sm text-white font-medium placeholder-gray-400"
                value={trigger}
                onChange={(e) => updateStartTrigger(idx, e.target.value)}
                placeholder="Trigger keyword"
                disabled={saving}
              />
              <button
                type="button"
                className="text-rose-400 hover:text-rose-300 hover:bg-white/10 p-1.5 rounded-lg transition-colors"
                onClick={() => removeStartTrigger(idx)}
                disabled={saving || (localConfig.startTriggers || []).length <= 1}
                title={(localConfig.startTriggers || []).length <= 1 ? "At least one trigger is required" : "Remove trigger"}
              >
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Add New Trigger */}
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white bg-white/5 focus:outline-none focus:ring-2 focus:ring-emerald-400/20 focus:border-emerald-400 transition-all placeholder-gray-400"
            value={newTrigger}
            onChange={(e) => setNewTrigger(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Add new trigger (e.g., hello, hi)"
            disabled={saving}
          />
          <button
            type="button"
            className="bg-emerald-600 text-white px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2 shadow-sm hover:shadow-md"
            onClick={addStartTrigger}
            disabled={saving || !newTrigger.trim()}
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
      </motion.div>

      {/* Welcome Message Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/5 rounded-lg p-5 shadow-md border border-white/10"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-white/10 rounded-lg">
            <MessageSquare className="w-4 h-4 text-emerald-300" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Welcome Message</h3>
            <p className="text-xs text-gray-600">First message users will see</p>
          </div>
        </div>
        <div>
          <label htmlFor="auto-message" className="block mb-2 text-sm font-medium text-gray-300">
            Initial greeting message
          </label>
          <textarea
            id="auto-message"
            rows={4}
            className="w-full border border-white/10 rounded-lg px-3 py-2.5 resize-none text-sm text-white bg-white/5 focus:outline-none focus:ring-2 focus:ring-emerald-400/20 focus:border-emerald-400 transition-all placeholder-gray-400"
            value={localConfig.autoMessage}
            onChange={(e) => handleChange("autoMessage", e.target.value)}
            placeholder="Enter the welcoming message your bot will send..."
            disabled={saving}
          />
        </div>
      </motion.div>

      {/* Response Flows Section */}
     <motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.3 }}
  className="bg-white/5 rounded-lg p-3 sm:p-5 shadow-md border border-white/10 w-full"
>
  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3 sm:gap-0">
    <div className="flex items-center gap-2">
      <div className="p-2 bg-white/10 rounded-lg flex-shrink-0">
        <MessageSquare className="w-4 h-4 text-emerald-300" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-base sm:text-lg font-semibold text-white truncate">
          Response Flows
        </h3>
        <p className="text-xs text-gray-300 hidden sm:block">
          Configure how your bot responds
        </p>
      </div>
    </div>
    <button
      type="button"
      className="bg-emerald-600 text-white px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 shadow-sm hover:shadow-md w-full sm:w-auto flex-shrink-0"
      onClick={addResponse}
      disabled={saving}
    >
      <Plus className="w-4 h-4" />
      <span className="sm:inline">Add</span>
    </button>
  </div>

  <div className="space-y-3 sm:space-y-4">
    {(localConfig.responses || []).map((resp, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="relative bg-white/5 border border-white/10 rounded-lg p-3 sm:p-4 hover:shadow-md transition-all"
      >
        <button
          type="button"
          className="absolute right-2 top-2 sm:right-3 sm:top-3 text-rose-400 hover:text-rose-300 hover:bg-white/10 p-1.5 rounded-lg transition-colors z-10"
          onClick={() => removeResponse(i)}
          disabled={saving}
          aria-label="Remove response"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="space-y-3 pr-8 sm:pr-12">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              User Input Trigger
            </label>
            <input
              type="text"
              className="w-full border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/20 focus:border-emerald-400 transition-all placeholder-gray-400 bg-white/5"
              placeholder="What user types (e.g., hello, help, pricing)"
              value={resp.trigger || ""}
              onChange={(e) => updateResponse(i, "trigger", e.target.value)}
              disabled={saving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Response Type
            </label>
            <select
              className="w-full border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/20 focus:border-emerald-400 transition-all bg-white/5"
              value={resp.action || "message"}
              onChange={(e) => updateResponse(i, "action", e.target.value)}
              disabled={saving}
            >
              <option value="message">Send Message</option>
              <option value="document">Send Document</option>
              <option value="default">Default Response</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {resp.action === "document" ? "Document URL" : "Response Content"}
            </label>
            <textarea
              rows={3}
              className="w-full border border-white/10 rounded-lg px-3 py-2.5 resize-none text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/20 focus:border-emerald-400 transition-all placeholder-gray-400 bg-white/5 min-h-[80px]"
              placeholder={
                resp.action === "document" 
                  ? "Enter document URL (e.g., https://example.com/file.pdf)" 
                  : "Enter the bot's response message"
              }
              value={resp.content || ""}
              onChange={(e) => updateResponse(i, "content", e.target.value)}
              disabled={saving}
            />
          </div>
        </div>
      </motion.div>
    ))}
  </div>

  {(localConfig.responses || []).length === 0 && (
    <div className="text-center py-6 sm:py-8 text-gray-400">
      <div className="p-3 sm:p-4 bg-white/5 rounded-lg inline-block mb-3">
        <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 mx-auto text-emerald-300" />
      </div>
      <p className="text-sm font-medium">No response flows yet</p>
      <p className="text-xs text-gray-400 mt-1 px-4">
        Add your first response to get started
      </p>
    </div>
  )}
</motion.div>


      {/* Status Messages */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-rose-500/10 border border-rose-400/30 text-rose-200 px-4 py-3 rounded-lg text-sm font-medium"
        >
          {error}
        </motion.div>
      )}
      
      {success && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 px-4 py-3 rounded-lg text-sm font-medium"
        >
          {success}
        </motion.div>
      )}

      {/* Save Button - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 lg:relative lg:bottom-auto bg-[#0B0F13] border-t lg:border-t-0 border-white/10 p-4 lg:p-0 shadow-lg lg:shadow-none">
        <motion.button
          type="button"
          onClick={handleSave}
          className="w-full bg-emerald-600 text-white py-3 rounded-lg hover:bg-emerald-700 font-semibold text-sm transition-colors flex items-center justify-center gap-2 shadow-md hover:shadow-lg disabled:opacity-50"
          disabled={saving}
          whileTap={{ scale: 0.98 }}
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {mode === "create" ? "Creating..." : "Saving..."}
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {mode === "create" ? "Create Chatbot" : "Save Changes"}
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
}
